import React from 'react';

export type BufferedInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  tagName?: string,
  value?: string,
  name: string,
  required?: boolean,
  onSave(value: string, name: string): void,
}

type BufferedInputState = {
  initialValue?: string,
  value?: string,
}

// BufferedInput is an <input that only saves when the user leaves the field, or
// when it unmounts.
//
// Save on unmount is a bit of a hack to accomodate required fields that
// validate on save. In the Editor, assign a field to Sender, then don't set a
// name. When you select a different field, unmounting the sidebar and this
// BufferedInput, it will trigger a save and you get a validation error.
class BufferedInput extends React.PureComponent<BufferedInputProps, BufferedInputState> {
  state = {
    initialValue: this.props.value,
    value: this.props.value,
  };

  static getDerivedStateFromProps(props: BufferedInputProps, state: BufferedInputState) {
    const value = props.value;

    // If the value was changed externally, update to the new value
    if (state.initialValue !== value) {
      return {
        initialValue: value,
        value,
      };
    }
    return null;
  }

  // HACK: If calling `onSave` on response to `onBlur` ends up unmounting this
  // component, `componentWillUnmount` fires before the state has updated. This
  // causes `onSave` to fire twice.
  changed = false;

  handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    this.changed = true;
    this.setState({
      value: event.target.value,
    });
  };

  handleSave = () => {
    // Validation may not kick in on fields that are required, but have not been touched by user
    const forceSave = this.props.required
      && (
        !this.state.value
        || (this.state.value.length == null || this.state.value.length === 0)
      );

    if (this.changed || forceSave) {
      this.changed = false;
      this.props.onSave(this.state.value || '', this.props.name);
    }
  };

  componentWillUnmount() {
    this.handleSave();
  }

  render() {
    const { tagName = 'input', value, onSave, ...props } = this.props;

    return React.createElement(tagName, {
      type: tagName === 'input' ? 'text' : undefined,
      ...props,
      // The empty string is necessary to make sure this is always a controlled
      // component.
      value: this.state.value || '',
      onBlur: this.handleSave,
      onChange: this.handleChange,
    });
  }
}

export default BufferedInput;
